import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { BRAND, DEMO_URL, AUDIENCES } from '../../data/dgpt';
import { CtaButton } from '../ui/CtaButton';
import { Eyebrow } from '../ui/Eyebrow';
import { VideoFacade } from './VideoFacade';
export function HeroSection() {
  const reduceMotion = useReducedMotion();
  const fadeUp = (delay = 0) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6,
      delay,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  };
  return <section id="top" aria-labelledby="hero-heading" className="relative overflow-hidden border-b border-hairline-soft bg-ink" data-id="element-141" data-mp="3iy5fhy8sd3g7">
      <div className="sf-grid-texture sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-142" data-mp="3rxih64qo4nqd" />
      <div aria-hidden="true" className="pointer-events-none absolute right-[-10%] top-[10%] h-[620px] w-[820px] rounded-full" style={{
      background: 'radial-gradient(circle, rgba(32,22,95,0.05), transparent 62%)'
    }} data-id="element-143" data-mp="rhas9hneib2" />

      <div className="relative mx-auto grid w-full max-w-[1320px] gap-12 px-4 pb-16 pt-12 sm:px-6 sm:pt-16 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-center lg:gap-14 lg:px-8 lg:pb-24 lg:pt-20" data-id="element-144" data-mp="240awjuka63gh">
        <div className="flex flex-col items-start" data-id="element-145" data-mp="2rniz44qbqyet">
          <motion.div {...fadeUp(0)} data-id="element-146" data-mp="2rmzjwvs26o4h">
            <Eyebrow data-id="element-147" data-mp="2mofuumi64w7w">Venture Intelligence Infrastructure</Eyebrow>
          </motion.div>

          <motion.h1 {...fadeUp(0.05)} id="hero-heading" className="mt-6 text-balance text-[42px] font-semibold leading-[1] tracking-[-0.04em] text-text-primary sm:text-[60px] lg:text-[68px]" data-id="element-148" data-mp="2mqsbmwc6x0jd">
            Super-powering Venture Capital
          </motion.h1>

          <motion.p {...fadeUp(0.1)} className="mt-5 text-[26px] font-medium leading-[1.12] tracking-[-0.03em] text-text-primary sm:text-[34px]" data-id="element-149" data-mp="3aftlmo0o8jqr">
            Diligence at the <span className="text-lime" data-id="element-150" data-mp="13s48072v0o2w">Speed of Dealflow.</span>
          </motion.p>

          <motion.p {...fadeUp(0.14)} className="mt-3 text-sm font-medium uppercase tracking-[0.14em] text-text-secondary" data-id="element-151" data-mp="17tfw2863518z">
            {BRAND.category}
          </motion.p>

          <motion.p {...fadeUp(0.18)} className="mt-6 max-w-xl text-[15px] leading-relaxed text-text-secondary sm:text-base" data-id="element-152" data-mp="ywouxhcoew7j">
            Screen, understand, compare and discover startups using proprietary startup
            intelligence, fund-specific intelligence, global dealflow and venture-specific AI.
          </motion.p>

          <motion.p {...fadeUp(0.2)} className="mt-4 border-l-2 border-lime/60 pl-3 text-sm text-text-primary" data-id="element-153" data-mp="g0p9kpe7eqwx">
            {BRAND.campaign}
          </motion.p>

          {/* Mobile/tablet: video sits above the hero CTA. */}
          <motion.div {...fadeUp(0.22)} className="mt-8 w-full lg:hidden" data-id="element-154" data-mp="2khlsybt3h81w">
            <VideoFacade data-id="element-155" data-mp="2fj0sqcdueodg" />
          </motion.div>

          <motion.div {...fadeUp(0.24)} className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row lg:mt-8" data-id="element-156" data-mp="2fkbsm1463bza">
            <CtaButton href={DEMO_URL} event="hero_book_demo_click" size="xl" className="w-full sm:w-auto" data-id="element-157" data-mp="2tbqd6cdq3l8g">
              Book a Demo
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-158" data-mp="338msb0y4n5lz" />
            </CtaButton>
          </motion.div>

          <motion.p {...fadeUp(0.28)} className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-tertiary" data-id="element-159" data-mp="2ya1okrjo23ss">
            <span className="text-text-secondary" data-id="element-160" data-mp="3c6yorjlocxmc">Built for</span>
            {AUDIENCES.map((audience, index) => <React.Fragment key={audience} data-id="element-161" data-mp="1hva2sxh0zusg" data-mpi={index}>
                {index > 0 ? <span aria-hidden="true" data-id="element-162" data-mp="1hvjxuzgxgs50">•</span> : null}
                <span data-id="element-163" data-mp="1du6tyuxeaz0q">{audience}</span>
              </React.Fragment>)}
          </motion.p>
        </div>

        <motion.div {...reduceMotion ? {} : {
        initial: {
          opacity: 0,
          y: 24
        },
        animate: {
          opacity: 1,
          y: 0
        },
        transition: {
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
        }
      }} className="hidden flex-col gap-4 lg:flex" data-id="element-164" data-mp="1f9spmag764de">
          <VideoFacade data-id="element-165" data-mp="wkw2cqj7wc4o" />
          <div className="flex flex-col items-start justify-between gap-4 rounded-xl border border-hairline bg-panel px-5 py-4 sm:flex-row sm:items-center" data-id="element-166" data-mp="wlno79n0ivhr">
            <div data-id="element-167" data-mp="15lark0zaybdg">
              <p className="text-base font-semibold tracking-[-0.02em] text-text-primary" data-id="element-168" data-mp="15ksmceunwmte">{BRAND.product}</p>
              <p className="text-sm text-text-secondary" data-id="element-169" data-mp="1acsswq58mdyy">{BRAND.tagline}</p>
            </div>
            <CtaButton href={DEMO_URL} event="hero_book_demo_click" className="w-full uppercase tracking-[0.06em] sm:w-auto" data-id="element-170" data-mp="cx54h7ks0aag">
              Book a Demo
              <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-171" data-mp="aaw4nz8bvwh9" />
            </CtaButton>
          </div>
        </motion.div>
      </div>
    </section>;
}